import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TAX_DUTIES_SLICE_NAME } from './constants';
import {
	TCountryVatRateListRequest,
	TCountryVatRateListResponse,
	TCreateOrUpdateSpecialTerritoryPostalCodeRequest,
	TCreateOrUpdateSpecialTerritoryRequest,
	TCustomsDutyRateListRequest,
	TCustomsDutyRateListResponse,
	TSpecialTerritoryListRequest,
	TSpecialTerritoryListResponse,
	TSpecialTerritoryPostalCodeListRequest,
	TSpecialTerritoryPostalCodeListResponse,
	TUpdateSpecialTerritoryPostalCodeRequest,
	TUpdateSpecialTerritoryRequest,
	THs6pCodeListRequest,
	THs6pCodeListResponse,
	TSupportedNonEuCountry,
	TTaxDutiesStore,
	TUploadCountryVatRatesRequest,
} from './types';

// ==========================================:
export const initialState: TTaxDutiesStore = {
	countryVatRateList: null,
	countryVatRateListLoading: false,
	countryVatRateLastListParams: null,
	countryVatRateSyncLoading: false,
	countryVatRateUploadLoading: false,

	nonEuCountryVatRateList: null,
	nonEuCountryVatRateListLoading: false,
	supportedNonEuCountryList: null,
	supportedNonEuCountryListLoading: false,

	hs6pCodeList: null,
	hs6pCodeListLoading: false,

	customsDutyRateList: null,
	customsDutyRateListLoading: false,

	specialTerritoryList: null,
	specialTerritoryListLoading: false,
	specialTerritoryLastListParams: null,
	specialTerritorySaveLoading: false,
	specialTerritorySaveSuccess: false,

	specialTerritoryPostalCodeList: null,
	specialTerritoryPostalCodeListLoading: false,
	specialTerritoryPostalCodeLastListParams: null,
	specialTerritoryPostalCodeSaveLoading: false,
	specialTerritoryPostalCodeSaveSuccess: false,
};

// ==========================================:
const taxDuties = createSlice({
	name: TAX_DUTIES_SLICE_NAME,
	initialState,
	reducers: {
		getCountryVatRateListRequest: (state, action: PayloadAction<TCountryVatRateListRequest>) => {
			state.countryVatRateListLoading = true;
			state.countryVatRateLastListParams = action.payload;
		},
		getCountryVatRateListSuccess: (state, action: PayloadAction<TCountryVatRateListResponse>) => {
			state.countryVatRateList = action.payload;
			state.countryVatRateListLoading = false;
		},
		getCountryVatRateListFailure: (state) => {
			state.countryVatRateListLoading = false;
		},

		syncCountryVatRatesRequest: (state) => {
			state.countryVatRateSyncLoading = true;
		},
		syncCountryVatRatesSuccess: (state) => {
			state.countryVatRateSyncLoading = false;
		},
		syncCountryVatRatesFailure: (state) => {
			state.countryVatRateSyncLoading = false;
		},

		uploadCountryVatRatesRequest: (
			state,
			_action: PayloadAction<TUploadCountryVatRatesRequest>,
		) => {
			state.countryVatRateUploadLoading = true;
		},
		uploadCountryVatRatesSuccess: (state) => {
			state.countryVatRateUploadLoading = false;
		},
		uploadCountryVatRatesFailure: (state) => {
			state.countryVatRateUploadLoading = false;
		},

		getNonEuCountryVatRateListRequest: (
			state,
			_action: PayloadAction<TCountryVatRateListRequest>,
		) => {
			state.nonEuCountryVatRateListLoading = true;
		},
		getNonEuCountryVatRateListSuccess: (
			state,
			action: PayloadAction<TCountryVatRateListResponse>,
		) => {
			state.nonEuCountryVatRateList = action.payload;
			state.nonEuCountryVatRateListLoading = false;
		},
		getNonEuCountryVatRateListFailure: (state) => {
			state.nonEuCountryVatRateListLoading = false;
		},

		getSupportedNonEuCountryListRequest: (state) => {
			state.supportedNonEuCountryListLoading = true;
		},
		getSupportedNonEuCountryListSuccess: (
			state,
			action: PayloadAction<TSupportedNonEuCountry[]>,
		) => {
			state.supportedNonEuCountryList = action.payload;
			state.supportedNonEuCountryListLoading = false;
		},
		getSupportedNonEuCountryListFailure: (state) => {
			state.supportedNonEuCountryListLoading = false;
		},

		getHs6pCodeListRequest: (state, _action: PayloadAction<THs6pCodeListRequest>) => {
			state.hs6pCodeListLoading = true;
		},
		getHs6pCodeListSuccess: (state, action: PayloadAction<THs6pCodeListResponse>) => {
			state.hs6pCodeList = action.payload;
			state.hs6pCodeListLoading = false;
		},
		getHs6pCodeListFailure: (state) => {
			state.hs6pCodeListLoading = false;
		},

		getCustomsDutyRateListRequest: (state, _action: PayloadAction<TCustomsDutyRateListRequest>) => {
			state.customsDutyRateListLoading = true;
		},
		getCustomsDutyRateListSuccess: (state, action: PayloadAction<TCustomsDutyRateListResponse>) => {
			state.customsDutyRateList = action.payload;
			state.customsDutyRateListLoading = false;
		},
		getCustomsDutyRateListFailure: (state) => {
			state.customsDutyRateListLoading = false;
		},

		getSpecialTerritoryListRequest: (
			state,
			action: PayloadAction<TSpecialTerritoryListRequest>,
		) => {
			state.specialTerritoryListLoading = true;
			state.specialTerritoryLastListParams = action.payload;
		},
		getSpecialTerritoryListSuccess: (
			state,
			action: PayloadAction<TSpecialTerritoryListResponse>,
		) => {
			state.specialTerritoryList = action.payload;
			state.specialTerritoryListLoading = false;
		},
		getSpecialTerritoryListFailure: (state) => {
			state.specialTerritoryListLoading = false;
		},

		createSpecialTerritoryRequest: (
			state,
			_action: PayloadAction<TCreateOrUpdateSpecialTerritoryRequest>,
		) => {
			state.specialTerritorySaveLoading = true;
			state.specialTerritorySaveSuccess = false;
		},
		updateSpecialTerritoryRequest: (
			state,
			_action: PayloadAction<TUpdateSpecialTerritoryRequest>,
		) => {
			state.specialTerritorySaveLoading = true;
			state.specialTerritorySaveSuccess = false;
		},
		saveSpecialTerritorySuccess: (state) => {
			state.specialTerritorySaveLoading = false;
			state.specialTerritorySaveSuccess = true;
		},
		saveSpecialTerritoryFailure: (state) => {
			state.specialTerritorySaveLoading = false;
		},
		resetSaveSpecialTerritorySuccess: (state) => {
			state.specialTerritorySaveSuccess = false;
		},

		getSpecialTerritoryPostalCodeListRequest: (
			state,
			action: PayloadAction<TSpecialTerritoryPostalCodeListRequest>,
		) => {
			state.specialTerritoryPostalCodeListLoading = true;
			state.specialTerritoryPostalCodeLastListParams = action.payload;
		},
		getSpecialTerritoryPostalCodeListSuccess: (
			state,
			action: PayloadAction<TSpecialTerritoryPostalCodeListResponse>,
		) => {
			state.specialTerritoryPostalCodeList = action.payload;
			state.specialTerritoryPostalCodeListLoading = false;
		},
		getSpecialTerritoryPostalCodeListFailure: (state) => {
			state.specialTerritoryPostalCodeListLoading = false;
		},
		clearSpecialTerritoryPostalCodeList: (state) => {
			state.specialTerritoryPostalCodeList = null;
			state.specialTerritoryPostalCodeLastListParams = null;
		},

		createSpecialTerritoryPostalCodeRequest: (
			state,
			_action: PayloadAction<TCreateOrUpdateSpecialTerritoryPostalCodeRequest>,
		) => {
			state.specialTerritoryPostalCodeSaveLoading = true;
			state.specialTerritoryPostalCodeSaveSuccess = false;
		},
		updateSpecialTerritoryPostalCodeRequest: (
			state,
			_action: PayloadAction<TUpdateSpecialTerritoryPostalCodeRequest>,
		) => {
			state.specialTerritoryPostalCodeSaveLoading = true;
			state.specialTerritoryPostalCodeSaveSuccess = false;
		},
		saveSpecialTerritoryPostalCodeSuccess: (state) => {
			state.specialTerritoryPostalCodeSaveLoading = false;
			state.specialTerritoryPostalCodeSaveSuccess = true;
		},
		saveSpecialTerritoryPostalCodeFailure: (state) => {
			state.specialTerritoryPostalCodeSaveLoading = false;
		},
		resetSaveSpecialTerritoryPostalCodeSuccess: (state) => {
			state.specialTerritoryPostalCodeSaveSuccess = false;
		},

		taxDutiesInitState: () => initialState,
	},
});

export default taxDuties.reducer;
export const {
	getCountryVatRateListRequest,
	getCountryVatRateListSuccess,
	getCountryVatRateListFailure,
	syncCountryVatRatesRequest,
	syncCountryVatRatesSuccess,
	syncCountryVatRatesFailure,
	uploadCountryVatRatesRequest,
	uploadCountryVatRatesSuccess,
	uploadCountryVatRatesFailure,
	getNonEuCountryVatRateListRequest,
	getNonEuCountryVatRateListSuccess,
	getNonEuCountryVatRateListFailure,
	getSupportedNonEuCountryListRequest,
	getSupportedNonEuCountryListSuccess,
	getSupportedNonEuCountryListFailure,
	getHs6pCodeListRequest,
	getHs6pCodeListSuccess,
	getHs6pCodeListFailure,
	getCustomsDutyRateListRequest,
	getCustomsDutyRateListSuccess,
	getCustomsDutyRateListFailure,
	getSpecialTerritoryListRequest,
	getSpecialTerritoryListSuccess,
	getSpecialTerritoryListFailure,
	createSpecialTerritoryRequest,
	updateSpecialTerritoryRequest,
	saveSpecialTerritorySuccess,
	saveSpecialTerritoryFailure,
	resetSaveSpecialTerritorySuccess,
	getSpecialTerritoryPostalCodeListRequest,
	getSpecialTerritoryPostalCodeListSuccess,
	getSpecialTerritoryPostalCodeListFailure,
	clearSpecialTerritoryPostalCodeList,
	createSpecialTerritoryPostalCodeRequest,
	updateSpecialTerritoryPostalCodeRequest,
	saveSpecialTerritoryPostalCodeSuccess,
	saveSpecialTerritoryPostalCodeFailure,
	resetSaveSpecialTerritoryPostalCodeSuccess,
	taxDutiesInitState,
} = taxDuties.actions;
